import { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSurvey } from '../contexts/SurveyContext';
import { Card, CardHeader, CardContent, CardContentWrapper } from './ui/Card';
import { Button } from './ui/Button';
import { IdleOverlay } from './IdleOverlay';

interface QuestionCardProps {
  onRestart: () => void;
}

export function QuestionCard({ onRestart }: QuestionCardProps) {
  const { getQuestions, getQuestionKeys, t } = useLanguage();
  const {
    currentQuestion,
    setAnswer,
    nextQuestion,
    previousQuestion,
  } = useSurvey();

  const [showConfirmation, setShowConfirmation] = useState(false);

  const questions = getQuestions();
  const questionKeys = getQuestionKeys();
  const currentQuestionText = questionKeys[currentQuestion];
  const currentOptions = questions[currentQuestionText] || [];

  const handleAnswerSelect = (answerValue: string) => {
    setAnswer(currentQuestion, answerValue);
    // Blur the button immediately to remove focus before advancing
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    nextQuestion();
  };

  const handleStartOver = () => {
    setShowConfirmation(true);
  };

  const confirmStartOver = () => {
    setShowConfirmation(false);
    onRestart();
  };

  const handleBackButton = () => {
    previousQuestion();
  };

  const cancelStartOver = () => {
    setShowConfirmation(false);
  };

  const questionNumber = currentQuestion + 1;

  if (showConfirmation) {
    return (
      <Card>
        <CardHeader>
          <div></div>
          <div></div>
        </CardHeader>

        <CardContent>
          <CardContentWrapper>
            <div className="text-center">
              <h2 className="text-l text-bold mb-xl">
                {t('confirmRestart')}
              </h2>
              <div className="flex-row gap-l center">
                <Button onClick={confirmStartOver}>
                  {t('confirmYes')}
                </Button>
                <Button onClick={cancelStartOver}>
                  {t('cancel')}
                </Button>
              </div>
            </div>
          </CardContentWrapper>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        {currentQuestion > 0 ? (
          <Button onClick={handleBackButton}>
            {t('prev')}
          </Button>
        ) : (
          <div></div>
        )}
        <Button onClick={handleStartOver}>
          {t('restart')}
        </Button>
      </CardHeader>

      <CardContent>
        <CardContentWrapper>
          <div className="mt-xl mb-xl">
            <h2 className="text-l text-bold mb-l">
              {questionNumber}. {currentQuestionText}
            </h2>
          </div>

          <div className="flex-column gap-l">
            {currentOptions.map((option, index) => (
              <Button
                key={index}
                variant="choice"
                onClick={() => handleAnswerSelect(String(index))}
              >
                {option}
              </Button>
            ))}
          </div>
        </CardContentWrapper>
      </CardContent>
      <IdleOverlay onRestart={onRestart} />
    </Card>
  );
}
